import { useState, useEffect, useCallback } from "react";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import UpgradeModal from "@/components/base/UpgradeModal";
import { checkLimit, bumpLimitCache, type LimitCheckResult } from "@/lib/planLimits";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logAudit } from "@/lib/logAudit";
import { createNotification } from "@/lib/createNotification";
import { createReminder, daysBefore, daysAfter } from "@/lib/createReminder";
import JobModal, { type Job, type Client, type Contract } from "./components/JobModal";
import { getTimestampsForStatus, getWorkflowStatus, type WorkflowStatus } from "@/lib/workflowStatus";
import WorkflowModal from "./components/WorkflowModal";
import PhotosModal from "./components/PhotosModal";
import DeliveriesModal from "./components/DeliveriesModal";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import StatusBadge from "@/components/base/StatusBadge";


const STATUS_LABELS: Record<string, string> = {
  scheduled:     "Scheduled",
  confirmed:     "Confirmed",
  shooting:      "Shooting",
  editing:       "Editing",
  waiting_client:"Waiting Client",
  rendering:     "Rendering",
  delivered:     "Delivered",
  completed:     "Completed",
  cancelled:     "Cancelled",
  // legacy
  in_progress:   "In Progress",
};

const STATUS_FILTERS = ["all", "scheduled", "confirmed", "shooting", "editing", "waiting_client", "rendering", "delivered", "completed", "cancelled"];

export default function AdminJobs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingNew, setPendingNew] = useState(false);
  const [deliveryBanner, setDeliveryBanner] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [workflowJob, setWorkflowJob] = useState<Job | null>(null);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [photosJob, setPhotosJob] = useState<Job | null>(null);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [deliveriesJob, setDeliveriesJob] = useState<Job | null>(null);
  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<LimitCheckResult | null>(null);

  const { checking: checkingPhotoModule, hasAccess: photoModuleActive } = useModuleAccess('photo_selection');
  const { checking: checkingDeliveryModule, hasAccess: deliveryModuleActive } = useModuleAccess('final_deliveries');

  useEffect(() => {
    if (searchParams.get("new") === "1") setPendingNew(true);
    if (searchParams.get("delivery") === "1") setDeliveryBanner(true);
  }, [searchParams]);

  useEffect(() => {
    if (!loading && pendingNew) { setPendingNew(false); handleNew(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pendingNew]);

  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [jobsRes, clientsRes, contractsRes] = await Promise.all([
        supabase.from("jobs").select("*").eq("tenant_id", tid).order("shoot_date", { ascending: true, nullsFirst: false }),
        supabase.from("clients").select("id, full_name").eq("tenant_id", tid).eq("status", "active").order("full_name"),
        supabase.from("contracts").select("id, title, contract_number, total_amount, balance_amount, status, payment_status").eq("tenant_id", tid).order("title"),
      ]);
      if (jobsRes.error) throw new Error(jobsRes.error.message);
      if (jobsRes.data) setJobs(jobsRes.data as Job[]);
      if (clientsRes.data) setClients(clientsRes.data as Client[]);
      if (contractsRes.data) setContracts(contractsRes.data as Contract[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load jobs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  const clientName = (id: string) => clients.find((c) => c.id === id)?.full_name ?? "—";
  const contractInfo = (id: string | null) => (id ? contracts.find((c) => c.id === id) ?? null : null);

  const balanceLabel = (contract: { total_amount: number; balance_amount: number } | null) => {
    if (!contract) return null;
    const bal = Number(contract.balance_amount);
    if (bal <= 0) return { label: "Pagado", cls: "bg-emerald-400/15 text-emerald-400" };
    if (bal < Number(contract.total_amount)) return { label: "Parcial", cls: "bg-amber-400/15 text-amber-400" };
    return { label: "Pendiente", cls: "bg-red-400/15 text-red-400" };
  };

  const activeFilters = [typeFilter !== "all", clientFilter !== "all", dateFrom !== "", dateTo !== ""].filter(Boolean).length;

  const clearFilters = () => {
    setTypeFilter("all");
    setClientFilter("all");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("all");
    setSearch("");
  };

  const filtered = jobs.filter((j) => {
    const matchSearch =
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      clientName(j.client_id).toLowerCase().includes(search.toLowerCase()) ||
      (j.location ?? "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || j.workflow_status === statusFilter || j.status === statusFilter;
    const matchType = typeFilter === "all" || j.job_type === typeFilter;
    const matchClient = clientFilter === "all" || j.client_id === clientFilter;
    const shootDate = j.shoot_date ? new Date(j.shoot_date) : null;
    const matchFrom = dateFrom === "" || (shootDate !== null && shootDate >= new Date(dateFrom));
    const matchTo = dateTo === "" || (shootDate !== null && shootDate <= new Date(dateTo + "T23:59:59"));
    return matchSearch && matchStatus && matchType && matchClient && matchFrom && matchTo;
  });

  const handleNew = async () => {
    const limitResult = await checkLimit('jobs');
    if (limitResult && !limitResult.allowed) {
      setUpgradeResult(limitResult);
      return;
    }
    setSelectedJob(null);
    setIsNew(true);
    setModalOpen(true);
  };

  const handleEdit = (job: Job) => {
    setSelectedJob(job);
    setIsNew(false);
    setModalOpen(true);
  };

  const handleDelete = async (job: Job) => {
    const result = await Swal.fire({
      title: "Delete Job?",
      text: `"${job.title}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed || !tenantId) return;
    const { error } = await supabase
      .from("jobs")
      .delete()
      .eq("id", job.id)
      .eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else {
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      logAudit({ tenantId, action: "job_deleted", entity: "job", entityId: job.id, entityName: job.title }).catch(() => {});
      Swal.fire({ title: "Deleted", text: "Job removed.", icon: "success", timer: 1500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    }
  };

  const DEFAULT_STEPS = [
    { step_key: "contract_deposit", title: "Contract / Deposit", sort_order: 0 },
    { step_key: "shoot_scheduled", title: "Shoot Scheduled", sort_order: 1 },
    { step_key: "shoot_completed", title: "Shoot Completed", sort_order: 2 },
    { step_key: "editing", title: "Editing", sort_order: 3 },
    { step_key: "client_selection", title: "Client Selection", sort_order: 4 },
    { step_key: "final_delivery", title: "Final Delivery", sort_order: 5 },
    { step_key: "completed", title: "Completed", sort_order: 6 },
  ];

  const handleWorkflow = (job: Job) => {
    setWorkflowJob(job);
    setWorkflowOpen(true);
  };

  const handlePhotos = (job: Job) => {
    setPhotosJob(job);
    setPhotosOpen(true);
  };

  const handleDeliveries = (job: Job) => {
    setDeliveriesJob(job);
    setDeliveriesOpen(true);
  };

  const handleSave = async (data: Job) => {
    if (!tenantId) return;
    if (isNew) {
      const { id: _id, created_at: _ca, updated_at: _ua, ...insertData } = data;
      const payload = {
        ...insertData,
        tenant_id: tenantId,
        workflow_status: insertData.workflow_status || insertData.status || "scheduled",
        contract_id: insertData.contract_id || null,
        shoot_date: insertData.shoot_date || null,
        location: insertData.location || null,
        notes: insertData.notes || null,
        editing_started_at: null,
        completed_at: null,
      };
      const { data: created, error } = await supabase
        .from("jobs")
        .insert(payload)
        .select()
        .maybeSingle();
      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
      if (created) {
        const newJob = created as Job;
        setJobs((prev) => [newJob, ...prev]);
        const stepsPayload = DEFAULT_STEPS.map((s) => ({
          ...s,
          tenant_id: tenantId,
          job_id: newJob.id,
          status: "pending",
        }));
        const { error: stepsError } = await supabase.from("job_steps").insert(stepsPayload);
        if (stepsError) {
          Swal.fire({ title: "Warning", text: `Job created but workflow steps failed: ${stepsError.message}`, icon: "warning", background: "#ffffff", color: "#0f172a" });
          return;
        }
      }
      if (created) {
        bumpLimitCache('jobs');
        logAudit({ tenantId, action: "job_created", entity: "job", entityId: (created as Job).id, entityName: data.title }).catch(() => {});
        createNotification({ tenantId, type: 'info', title: 'New job created', message: `Job "${data.title}" has been scheduled successfully.` });
        // Auto-reminder: 1 day before shoot
        const createdJob = created as Job;
        if (createdJob.shoot_date) {
          createReminder({
            tenantId,
            reminderType: 'upcoming_shoot',
            entityType: 'job',
            entityId: createdJob.id,
            reminderDate: daysBefore(createdJob.shoot_date, 1),
            message: `Upcoming shoot tomorrow: "${createdJob.title}" — confirm venue, equipment and timeline.`,
          });
        }
      }
      Swal.fire({ title: "Job Created", text: "New job added with workflow steps.", icon: "success", timer: 1800, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    } else {
      const now = new Date().toISOString();
      const wfStatus = (data.workflow_status || data.status) as WorkflowStatus;
      const wfTimestamps = getTimestampsForStatus(wfStatus, now);
      const { data: updated, error } = await supabase
        .from("jobs")
        .update({
          title: data.title,
          client_id: data.client_id,
          contract_id: data.contract_id || null,
          job_type: data.job_type,
          status: data.status,
          workflow_status: wfStatus,
          shoot_date: data.shoot_date || null,
          location: data.location || null,
          notes: data.notes || null,
          updated_at: now,
          ...wfTimestamps,
        })
        .eq("id", data.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
      if (updated) {
        setJobs((prev) => prev.map((j) => (j.id === (updated as Job).id ? (updated as Job) : j)));
        const prevStatus = selectedJob?.workflow_status || selectedJob?.status;
        logAudit({ tenantId, action: "job_updated", entity: "job", entityId: data.id, entityName: data.title, details: { status_changed: prevStatus !== wfStatus, from: prevStatus, to: wfStatus } }).catch(() => {});
        if (selectedJob && selectedJob.workflow_status !== wfStatus) {
          createNotification({ tenantId, type: 'info', title: 'Job status updated', message: `Job "${data.title}" moved to ${wfStatus.replace(/_/g, ' ')}.` });
          if (wfStatus === 'editing' || wfStatus === 'waiting_client') {
            createReminder({
              tenantId,
              reminderType: 'photo_selection',
              entityType: 'job',
              entityId: data.id,
              reminderDate: daysAfter(new Date().toISOString(), 3),
              message: `Photo selection pending for "${data.title}" — follow up with client if not completed.`,
            });
          }
        }
      }
      Swal.fire({ title: "Saved", text: "Job updated successfully.", icon: "success", timer: 1500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    }
    setModalOpen(false);
  };

  return (
    <AdminLayout>
      <AdminHeader
        title="Jobs / Shoots"
        subtitle="Schedule and track your photography sessions"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965e] text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </span>
            Create Job
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">
        {/* Delivery banner */}
        {deliveryBanner && (
          <div className="flex items-center gap-3 mb-5 px-4 py-3 bg-emerald-400/8 border border-emerald-400/20 rounded-xl">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-400 shrink-0">
              <i className="ri-send-plane-line text-sm"></i>
            </span>
            <p className="text-emerald-300 text-sm flex-1">
              <span className="font-semibold">Nueva Entrega</span> — Haz clic en el icóno
              <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 bg-emerald-400/15 rounded text-xs">
                <i className="ri-send-plane-line text-xs"></i>
              </span>
              de cualquier job para agregar su entrega.
            </p>
            <button
              onClick={() => setDeliveryBanner(false)}
              className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-500 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        )}
        {/* Filters */}
        <div className="flex flex-col gap-3 mb-6">
          {/* Row 1: Search + results count + clear */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search jobs, clients, locations…"
                className="w-full bg-white rounded-xl border border-slate-200 pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>

            {/* Job Type */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
            >
              <option value="all">All Types</option>
              {["Wedding","Portrait","Engagement","Family","Newborn","Event","Corporate","Product","Real Estate","Boudoir","Other"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Client */}
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer max-w-[180px]"
            >
              <option value="all">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.full_name}</option>
              ))}
            </select>

            {/* Date From */}
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-xs whitespace-nowrap">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-xs whitespace-nowrap">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              />
            </div>

            {/* Clear filters */}
            {(activeFilters > 0 || search !== "") && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 bg-red-400/10 hover:bg-red-400/15 border border-red-400/20 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-off-line text-sm"></i>
                </span>
                Limpiar
                {activeFilters > 0 && (
                  <span className="bg-red-400/20 text-red-300 text-xs px-1.5 py-0.5 rounded-full">{activeFilters}</span>
                )}
              </button>
            )}

            <span className="text-slate-400 text-sm ml-auto whitespace-nowrap">{filtered.length} resultados</span>
          </div>

          {/* Row 2: Status tabs */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1 flex-wrap overflow-x-auto">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  statusFilter === s ? "bg-[#C8A96E] text-black" : "text-slate-400 hover:text-[#0f172a]"
                }`}
              >
                {s === "all" ? "Todos" : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
          ) : (
            <div className="overflow-x-auto"><table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Client</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Title</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Type</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Workflow</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Selection</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Delivery</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Shoot Date</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Contract</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Balance</th>
                  <th className="px-5 py-3.5 text-right text-slate-400 text-xs font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((job) => (
                  <tr key={job.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#C8A96E] text-xs font-semibold shrink-0">
                          {clientName(job.client_id).charAt(0)}
                        </div>
                        <span className="text-[#0f172a]/80 text-sm truncate max-w-[120px]">
                          {clientName(job.client_id)}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-[#0f172a] text-sm font-medium truncate max-w-[180px]">{job.title}</p>
                      {job.location && (
                        <p className="text-slate-400 text-xs truncate max-w-[180px] mt-0.5">
                          <span className="inline-flex items-center gap-0.5">
                            <i className="ri-map-pin-line text-[10px]"></i>
                            {job.location}
                          </span>
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-500 text-sm">{job.job_type}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const ws = job.workflow_status || job.status;
                        const cfg = getWorkflowStatus(ws);
                        return (
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border whitespace-nowrap ${cfg.bg} ${cfg.border} ${cfg.color}`}>
                            <i className={`${cfg.icon} text-[11px]`}></i>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3.5">
                      {job.photo_selection_status && job.photo_selection_status !== "not_started" ? (
                        <span className={`
                          text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize whitespace-nowrap inline-flex items-center gap-1
                          ${job.photo_selection_status === "submitted"
                            ? "bg-amber-50 border-amber-200 text-amber-600"
                            : job.photo_selection_status === "reviewed"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                              : job.photo_selection_status === "in_progress"
                                ? "bg-blue-50 border-blue-200 text-blue-600"
                                : "bg-slate-50 border-slate-200 text-slate-500"}
                        `}>
                          <i className={
                            job.photo_selection_status === "submitted" ? "ri-time-line" :
                            job.photo_selection_status === "reviewed" ? "ri-check-double-line" :
                            job.photo_selection_status === "in_progress" ? "ri-loader-4-line" :
                            "ri-image-2-line"
                          }></i>
                          {job.photo_selection_status.replace(/_/g, " ")}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {job.delivery_status && job.delivery_status !== "not_started" ? (
                        <span className={`
                          text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize whitespace-nowrap inline-flex items-center gap-1
                          ${job.delivery_status === "delivered"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                            : job.delivery_status === "prepared"
                              ? "bg-sky-50 border-sky-200 text-sky-600"
                              : job.delivery_status === "expired"
                                ? "bg-red-50 border-red-200 text-red-500"
                                : "bg-slate-50 border-slate-200 text-slate-500"}
                        `}>
                          <i className={
                            job.delivery_status === "delivered" ? "ri-check-double-line" :
                            job.delivery_status === "prepared" ? "ri-archive-line" :
                            job.delivery_status === "expired" ? "ri-time-line" :
                            "ri-send-plane-line"
                          }></i>
                          {job.delivery_status.replace(/_/g, " ")}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {job.shoot_date ? (
                        <div>
                          <p className="text-slate-600 text-sm">
                            {new Date(job.shoot_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {new Date(job.shoot_date).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const c = contractInfo(job.contract_id);
                        if (!c) return <span className="text-slate-300 text-xs">Sin contrato</span>;
                        return (
                          <div>
                            <p className="text-[#0f172a]/80 text-sm font-medium truncate max-w-[160px]">{c.title}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-slate-400 text-xs font-mono">{c.contract_number}</span>
                              <StatusBadge status={c.status} size="xs" showIcon={false} />
                            </div>
                            <div className="mt-1">
                              <PaymentStatusBadge status={(c as Record<string, string | undefined>).payment_status} />
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const c = contractInfo(job.contract_id);
                        if (!c) return <span className="text-slate-300 text-xs">—</span>;
                        const bal = balanceLabel(c);
                        return (
                          <div>
                            <p className="text-[#0f172a] text-sm font-semibold">${Number(c.balance_amount).toLocaleString()}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-slate-400 text-xs">de ${Number(c.total_amount).toLocaleString()}</span>
                              {bal && (
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${bal.cls}`}>
                                  {bal.label}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/admin/jobs/${job.id}`)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                          title="View Detail"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        {(checkingDeliveryModule || deliveryModuleActive) && (
                        <button
                          onClick={() => handleDeliveries(job)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                          title="Deliveries"
                        >
                          <i className="ri-send-plane-line text-sm"></i>
                        </button>
                        )}
                        {(checkingPhotoModule || photoModuleActive) && (
                          <button
                            onClick={() => handlePhotos(job)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                            title="Photos"
                          >
                            <i className="ri-image-2-line text-sm"></i>
                          </button>
                        )}
                        <button
                          onClick={() => handleWorkflow(job)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                          title="Workflow"
                        >
                          <i className="ri-git-branch-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleEdit(job)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(job)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} className="py-16">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
                          <i className="ri-camera-3-line text-[#C8A96E] text-2xl"></i>
                        </div>
                        <div>
                          <p className="text-[#0f172a] font-medium text-sm mb-1">
                            {jobs.length === 0 ? "No jobs yet" : "No jobs match your filters"}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {jobs.length === 0
                              ? "Create your first job to start tracking production, photos, and deliveries."
                              : "Try adjusting the filters or search term."}
                          </p>
                        </div>
                        {jobs.length === 0 && (
                          <button
                            onClick={handleNew}
                            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965e] text-black text-sm font-semibold px-5 py-2 rounded-lg mt-1 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-add-line"></i>
                            Create First Job
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

      {upgradeResult && (
        <UpgradeModal
          result={upgradeResult}
          onClose={() => setUpgradeResult(null)}
        />
      )}

      {modalOpen && (
        <JobModal
          job={selectedJob}
          isNew={isNew}
          clients={clients}
          contracts={contracts}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
      {workflowOpen && workflowJob && tenantId && (
        <WorkflowModal
          job={workflowJob}
          tenantId={tenantId}
          onClose={() => { setWorkflowOpen(false); setWorkflowJob(null); }}
        />
      )}
      {photosOpen && photosJob && tenantId && (
        <PhotosModal
          job={photosJob}
          tenantId={tenantId}
          clientName={clientName(photosJob.client_id)}
          onClose={() => { setPhotosOpen(false); setPhotosJob(null); }}
        />
      )}
      {deliveriesOpen && deliveriesJob && tenantId && (
        <DeliveriesModal
          job={deliveriesJob}
          tenantId={tenantId}
          clientName={clientName(deliveriesJob.client_id)}
          onClose={() => { setDeliveriesOpen(false); setDeliveriesJob(null); }}
        />
      )}
    </AdminLayout>
  );
}
